import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import classNames from 'classnames';
import { offset, flip, useFloating, useClick, useDismiss, useRole, useInteractions, autoUpdate } from '@floating-ui/react';
import { getOppositeAlignmentPlacement, getOppositePlacement } from '@floating-ui/utils';
import Button from '@/component/Button';
import ChevronArrowIcon from '@/component/icon/ChevronArrowIcon';


const getItemClassName = (selected: boolean, focused?: boolean) => {
    const selectedClassNames = "border-chat42-50 bg-chat42-10 text-chat42";
    const focusedClassNames = "border-black-200 bg-chat42-5 text-black-500";
    const defaultClassNames = "border-black-200 bg-white text-black-500";

    return classNames(
        "w-full py-2 pl-3.5 pr-2.5 border rounded-[6px] flex justify-between items-center cursor-pointer text-[14px] font-[400]",
        selected && selectedClassNames,
        !selected && focused && focusedClassNames,
        !selected && !focused && defaultClassNames
    );
};

export interface NestedDropdownOption<T extends string = string, U extends string = string> {
    primary: T;
    children?: React.ReactNode;
    secondary?: U[];
}

interface NestedDropdownProps {
    width?: number;
    options: NestedDropdownOption[];
    selectedKeys: {
        primary: number;
        secondary?: number;
    };
    'data-testid'?: string;
    'aria-label'?: string;
    onSelect: (primary: number, secondary?: number) => void;
}

const NestedDropdown: React.FC<NestedDropdownProps> = (props) => {
    const { width, options, selectedKeys, onSelect } = props;

    const intl = useIntl();
    const [isOpen, setIsOpen] = useState(false);

    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        placement: "bottom",
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(4),
            flip({
                fallbackPlacements: [getOppositePlacement("bottom")],
            }),
        ],
    });
    const click = useClick(context);
    const dismiss = useDismiss(context);
    const role = useRole(context);
    const { getReferenceProps, getFloatingProps } = useInteractions([ click, dismiss, role ]);

    const menuRef = useRef<HTMLDivElement>(null);

    const handleSelect = useCallback((primary: number, secondary?: number) => {
        if (primary === undefined) {
            return;
        }
        if (secondary === undefined && !!options[primary].secondary?.length) {
            return;
        }
        onSelect(primary, secondary);
        setIsOpen(false);
    }, [onSelect, options]);

    const selectedLabel = useMemo(() => {
        if (selectedKeys.primary === undefined) {
            return intl.formatMessage({ id: "b0020" });
        }
        if (selectedKeys.secondary === undefined) {
            return options[selectedKeys.primary].children ?? options[selectedKeys.primary].primary;
        }
        return options[selectedKeys.primary].primary + " > " + options[selectedKeys.primary].secondary?.[selectedKeys.secondary];
    }, [intl, options, selectedKeys]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div ref={menuRef}
             className="inline-block"
             style={{ width }}
        >
            <Button ref={refs.setReference}
                    className={getItemClassName(isOpen)}
                    {...getReferenceProps()}
                    data-testid={props['data-testid']}
                    aria-label={props['aria-label']}
            >
                <div className="text-[14px] font-[400]">
                    {selectedLabel}
                </div>
                <ChevronArrowIcon direction={isOpen ? "up" : "down"}
                                  className="size-4"
                />
            </Button>

            {isOpen && (
                <div ref={refs.setFloating}
                     className="w-full flex flex-col z-50"
                     style={{ ...floatingStyles, width }}
                     {...getFloatingProps()}
                >
                    <PrimaryOptions options={options}
                                    selectedKeys={selectedKeys}
                                    onSelect={handleSelect}
                    />
                </div>
            )}
        </div>
    );
};

interface PrimaryOptionsProps {
    options: NestedDropdownOption[];
    selectedKeys: {
        primary: number;
        secondary?: number;
    };
    onSelect: (primary: number, secondary?: number) => void;
}

const PrimaryOptions: React.FC<PrimaryOptionsProps> = ({ options, selectedKeys, onSelect }) => {
    const [activeIndex, setActiveIndex] = useState<number>();
    const [isSecondaryOpen, setIsSecondaryOpen] = useState(false);

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (isSecondaryOpen) {
                return;
            }
            switch (e.key) {
                case 'ArrowUp':
                case 'ArrowDown':
                    e.preventDefault();
                    if (activeIndex === undefined) {
                        setActiveIndex((e.key === "ArrowUp" ? options.length - 1 : 0));
                        return;
                    } else {
                        setActiveIndex((activeIndex + (e.key === "ArrowUp" ? -1 : 1) + options.length) % options.length);
                    }
                    break;
                case 'ArrowRight':
                    e.preventDefault();
                    if (activeIndex !== undefined && options[activeIndex].secondary?.length) {
                        setIsSecondaryOpen(true);
                    }
                    break;
                case ' ':
                case 'Enter':
                    e.preventDefault();
                    if (activeIndex !== undefined) {
                        onSelect(activeIndex);
                    }
                    break;
            }
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [options, activeIndex, isSecondaryOpen, onSelect]);

    return (
        <ul>
            {options.map((option, index) => (
                <PriamryOption key={option.primary}
                               option={option}
                               index={index}
                               isFocused={activeIndex === index}
                               isSelected={selectedKeys.primary === index}
                               isOptionActive={isSecondaryOpen}
                               setIsOptionActive={setIsSecondaryOpen}
                               selectedOptionIndex={selectedKeys.secondary}
                               onSelect={onSelect}
                               onMouseEnter={() => setActiveIndex(index)}
                />
            ))}
        </ul>
    );
};

interface PriamryOptionProps {
    option: NestedDropdownOption;
    index: number;
    isFocused: boolean;
    isSelected: boolean;
    isOptionActive: boolean;
    selectedOptionIndex?: number;
    setIsOptionActive: (isActive: boolean) => void;
    onSelect: (primary: number, secondary?: number) => void;
    onMouseEnter: () => void;
}

const PriamryOption: React.FC<PriamryOptionProps> = (props) => {
    const {
        option,
        index: priIndex,
        isFocused,
        isSelected,
        isOptionActive,
        setIsOptionActive,
        selectedOptionIndex: selectedSecondaryIndex,
        onSelect,
        onMouseEnter
    } = props;

    const optionsCount = option.secondary?.length ?? 0;

    const [activeOptionIndex, setActiveOptionIndex] = useState<number | undefined>();

    const { refs, floatingStyles, context } = useFloating({
        open: isFocused && optionsCount > 0,
        placement: "right-start",
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(2),
            flip({
                fallbackPlacements: [getOppositeAlignmentPlacement("right-start")],
            })
        ],
    });
    const click = useClick(context);
    const dismiss = useDismiss(context);
    const role = useRole(context);
    const { getReferenceProps, getFloatingProps } = useInteractions([ click, dismiss, role ]);

    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if (!isFocused || !isOptionActive) {
            return;
        }
        switch (e.key) {
            case 'ArrowUp':
            case 'ArrowDown':
                e.preventDefault();
                const step = e.key === "ArrowUp" ? -1 : 1;
                setActiveOptionIndex((activeOptionIndex! + step + optionsCount) % optionsCount);
                break;
            case 'ArrowLeft':
                e.preventDefault();
                setIsOptionActive(false);
                break;
            case ' ':
            case 'Enter':
                e.preventDefault();
                e.stopPropagation();
                onSelect(priIndex, activeOptionIndex);
                break;
        }
    }, [isFocused, isOptionActive, activeOptionIndex, optionsCount, setIsOptionActive, onSelect, priIndex]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    useEffect(() => {
        if (isOptionActive) {
            setActiveOptionIndex(prev => prev === undefined ? 0 : prev);
        } else {
            setActiveOptionIndex(undefined);
        }
    }, [isOptionActive]);

    return (<>
        <li ref={refs.setReference}
            className={classNames(getItemClassName(isSelected, isFocused))}
            {...getReferenceProps()}
            onMouseEnter={() => {
                setActiveOptionIndex(undefined);
                setIsOptionActive(false);
                onMouseEnter();
            }}
            onClick={() => onSelect(priIndex)}
        >
            {option.children ?? option.primary}
        </li>

        {isFocused && optionsCount > 0 && (
            <ul ref={refs.setFloating}
                className="w-full flex flex-col"
                style={{ ...floatingStyles }}
                {...getFloatingProps()}
            >
                {option.secondary!.map((sec, secIndex) => (
                    <li key={secIndex}
                        className={classNames(getItemClassName(
                            isSelected && selectedSecondaryIndex === secIndex,
                            activeOptionIndex === secIndex
                        ))}
                        onClick={e => {
                            e.stopPropagation();
                            onSelect(priIndex, secIndex);
                        }}
                        onMouseEnter={() => {
                            setActiveOptionIndex(secIndex);
                            setIsOptionActive(true);
                        }}
                    >
                        {sec}
                    </li>
                ))}
            </ul>
        )}
    </>);
};

export default NestedDropdown;
